import MenuIcon from '@mui/icons-material/Menu'
import PauseIcon from '@mui/icons-material/Pause'
import PlayIcon from '@mui/icons-material/PlayArrow'
import RestartIcon from '@mui/icons-material/RestartAlt'
import ShuffleIcon from '@mui/icons-material/Shuffle'
import {
  alpha,
  AppBar,
  Box,
  Button,
  createTheme,
  CssBaseline,
  IconButton,
  IconButtonProps,
  ListSubheader,
  Stack,
  styled,
  ThemeProvider,
  Toolbar,
  Typography,
} from '@mui/material'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import CanvasRenderer from 'Graphics/Renderers/CanvasRenderer'
import { Config, ConfigValues, NumberValue } from 'Playground/Config'
import RenderingCanvas from 'Playground/RenderingCanvas'
import { useState } from 'react'
import { BooleanParam, useQueryParam, withDefault } from 'use-query-params'
import ControlPanel from './ControlPanel'
import Project from './Project'

const SecondaryControlButton = styled(IconButton)<IconButtonProps>(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.default, 0.9),
  margin: 5,
  '&:hover, &.Mui-focusVisible': {
    backgroundColor: theme.palette.background.default,
  },
  '& svg': {
    margin: 5,
    fontSize: 40,
  },
}))

const PrimaryControlButton = styled(SecondaryControlButton)<IconButtonProps>(({ theme }) => ({
  '& svg': {
    fontSize: 50,
  },
}))

interface PlaygroundConfig extends ConfigValues {
  readonly animationLength: NumberValue // percent value of the animation length within the valid range
}

interface PlaygroundProps {
  project: Project<any, any>
}

function Playground<T extends ConfigValues>({ project }: PlaygroundProps) {
  // Default theme
  const theme = createTheme({
    palette: {
      mode: 'dark',
      background: {
        default: '#222222',
      },
    },
  })
  // Playground config
  const defaultPlaygroundConfig = new Config({
    animationLength: new NumberValue(
      20, // min, seconds
      60, // default, seconds
      120, // max, seconds
    ),
  } as PlaygroundConfig)
  const [playgroundConfig, setPlaygroundConfig] = useQueryParam('p', defaultPlaygroundConfig.asQueryParam())
  // Project config
  const [projectConfig, setProjectConfig] = useQueryParam('c', project.defaultConfig.asQueryParam())
  const [projectDebugConfig, setProjectDebugConfig] = useQueryParam('d', project.defaultDebugConfig.asQueryParam())
  // Other state
  const [isAnimationActive, setIsAnimationActive] = useState(false)
  const [showDebugControls] = useQueryParam('debug', withDefault(BooleanParam, process.env.NODE_ENV != 'production'))
  const [showMenu, setShowMenu] = useState(false)
  // Default renderer
  const renderer = new CanvasRenderer()

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Stack sx={{ width: 1, height: 1, position: 'absolute' }}>
        <AppBar position='static' sx={{ zIndex: theme.zIndex.drawer + 1 }}>
          <Toolbar variant='dense'>
            <IconButton
              size='large'
              edge='start'
              color='inherit'
              aria-label='menu'
              sx={{ mr: 1, display: 'flex' }}
              onClick={() => setShowMenu(!showMenu)}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
              Stars
            </Typography>
            <Button
              color='inherit'
              href='https://x.com/tokudu'
              target='_blank'
              rel='noopener noreferrer'
              size='small'
              startIcon={
                <svg viewBox='0 0 24 24' width='16' height='16' fill='currentColor'>
                  <path d='M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' />
                </svg>
              }
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Follow @tokudu
            </Button>
          </Toolbar>
        </AppBar>
        <SwipeableDrawer
          anchor='left'
          open={showMenu}
          onClose={() => setShowMenu(false)}
          onOpen={() => setShowMenu(true)}
          hideBackdrop={true}
          sx={{ mt: theme.mixins.toolbar.height }}
        >
          <Box sx={{ width: 250 }} role='presentation'>
            <Toolbar variant='dense' />
            <ListSubheader component='div'>About</ListSubheader>
            <Box sx={{ pl: 2, pr: 2, pb: 1, pt: 1 }}>
              <Button
                variant='outlined'
                color='inherit'
                size='small'
                fullWidth
                href='https://cs.uwaterloo.ca/~csk/other/starpatterns/'
                target='_blank'
                rel='noopener noreferrer'
              >
                Learn more
              </Button>
            </Box>
            <ListSubheader component='div'>Configuration</ListSubheader>
            {playgroundConfig.keys.length > 0 && (
              <ControlPanel
                config={playgroundConfig}
                defaultConfig={defaultPlaygroundConfig}
                onConfigChange={(config) => setPlaygroundConfig(config)}
                disabled={isAnimationActive}
              />
            )}
            {projectConfig.keys.length > 0 && (
              <ControlPanel
                config={projectConfig}
                defaultConfig={project.defaultConfig}
                onConfigChange={(config) => setProjectConfig(config)}
                disabled={isAnimationActive}
              />
            )}
            {showDebugControls && projectDebugConfig.keys.length > 0 && (
              <ControlPanel
                title='Developer Tools'
                config={projectDebugConfig}
                defaultConfig={project.defaultDebugConfig}
                onConfigChange={(config) => setProjectDebugConfig(config)}
                disabled={isAnimationActive}
              />
            )}
          </Box>
        </SwipeableDrawer>
        <Box sx={{ flexGrow: 1, position: 'relative' }}>
          <RenderingCanvas
            sx={{ width: 1, height: 1, position: 'absolute' }}
            renderer={renderer}
            animationLengthMillis={isAnimationActive ? playgroundConfig.values.animationLength.val * 1000 : undefined}
            onRefresh={(width, height, animationPosition) => {
              const config = animationPosition !== undefined ? projectConfig.animate(animationPosition) : projectConfig
              return project.renderObject(width, height, config, projectDebugConfig)
            }}
          />
          <Box
            sx={{
              width: 1,
              bottom: 0,
              position: 'absolute',
              justifyContent: 'center',
              display: project.controlsEnabled ? 'inline-flex' : 'none',
              alignItems: 'center',
            }}
          >
            <SecondaryControlButton onClick={() => setIsAnimationActive(!isAnimationActive)}>
              {isAnimationActive ? <PauseIcon /> : <PlayIcon />}
            </SecondaryControlButton>
            <PrimaryControlButton size='large' onClick={() => setProjectConfig(projectConfig.randomize())}>
              <ShuffleIcon />
            </PrimaryControlButton>
            <SecondaryControlButton onClick={() => setProjectConfig(project.defaultConfig)}>
              <RestartIcon />
            </SecondaryControlButton>
          </Box>
        </Box>
      </Stack>
    </ThemeProvider>
  )
}

export default Playground
